/**
 * NBC REST API Client
 *
 * HTTP client for NBC API with retry logic and error handling.
 * Uses universal RestClient with axios-retry for exponential backoff.
 *
 * NBC API provides supplementary product data that enriches Player API
 * information. All NBC data is optional - application works without it.
 *
 * @module services/api/nbc/nbcClient
 */

import { RestClient } from '../shared/restClient';
import { NBC_CONFIG } from './config';

/**
 * Singleton instance of NBC API client
 *
 * Uses universal RestClient configured for NBC API.
 * Provides retry logic, error handling, and schema validation.
 *
 * @example Basic usage
 * const product = await nbcClient.get({
 *   endpoint: '/product/3123123',
 *   responseSchema: NBCSingleProductResponseSchema,
 * });
 *
 * @example With error handling
 * try {
 *   const products = await nbcClient.get({
 *     endpoint: '/product-list',
 *     params: { ids: '123,456,789' },
 *     responseSchema: NBCProductListResponseSchema,
 *   });
 * } catch (error) {
 *   if (error instanceof NetworkError) {
 *     console.error('NBC API unavailable:', error.message);
 *     // Continue without NBC data
 *   }
 * }
 */
export const nbcClient = new RestClient({
  baseURL: NBC_CONFIG.ENDPOINT,
  timeout: NBC_CONFIG.TIMEOUT,
  retryConfig: {
    retries: NBC_CONFIG.RETRY.maxAttempts,
    retryDelay: (retryCount) => {
      // Exponential backoff: 500ms → 1000ms
      return NBC_CONFIG.RETRY.initialDelay * Math.pow(2, retryCount - 1);
    },
  },
});
